import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Reorder, useDragControls } from 'framer-motion';
import { generateBracket } from '../../features/bracket/bracketSlice';
import { BracketTypeCard, BracketWizard, MatchSelect, WizardHint } from './bracketWizard';
import { matchesLabel, roundsLabel, shuffle, summarizeTeamRows } from './bracketPairings';
import { TEAM_BRACKET_TYPES, defaultTeamType, findTeamType } from './bracketTypes';

let rowSequence = 0;

const newRow = () => {
  rowSequence += 1;
  return { id: `team-row-${rowSequence}`, team1Id: '', team2Id: '' };
};

const TeamRow = ({
  row,
  index,
  isLast,
  options1,
  options2,
  onTeamChange,
  onClear
}) => {
  const controls = useDragControls();
  const hasValue = Boolean(row.team1Id || row.team2Id);

  return (
    <Reorder.Item
      as="div"
      value={row}
      dragListener={false}
      dragControls={controls}
      style={{ marginBottom: isLast ? 0 : 8 }}
    >
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, sm: 1.25 },
          p: { xs: 1, sm: 1.25 },
          borderRadius: 2.5,
          bgcolor: 'background.paper'
        }}
      >
        <Box
          onPointerDown={(event) => controls.start(event)}
          sx={{ display: 'flex', flexShrink: 0, cursor: 'grab', color: 'text.disabled', touchAction: 'none' }}
          aria-label={`Reorder match ${index + 1}`}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Typography variant="caption" sx={{ width: 18, flexShrink: 0, fontWeight: 700, color: 'text.secondary' }}>
          {index + 1}
        </Typography>
        <MatchSelect
          value={row.team1Id}
          onChange={(value) => onTeamChange(row.id, 'team1Id', value)}
          placeholder="Select team"
          options={options1}
        />
        <Typography variant="caption" sx={{ flexShrink: 0, fontWeight: 600, color: 'text.secondary' }}>
          vs
        </Typography>
        <MatchSelect
          value={row.team2Id}
          onChange={(value) => onTeamChange(row.id, 'team2Id', value)}
          placeholder="To be determined"
          options={options2}
        />
        <IconButton
          size="small"
          onClick={() => onClear(row.id)}
          disabled={!hasValue}
          aria-label={`Clear match ${index + 1}`}
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Reorder.Item>
  );
};

const GenerateBracketFromPlayers = ({ event, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const generating = useSelector((state) => state.bracket.generating);
  const teams = useMemo(() => event.teams || [], [event.teams]);
  const [name, setName] = useState(`${event.name || 'Event'} Bracket`);
  const [type, setType] = useState(() => defaultTeamType(teams.length));
  const [activeStep, setActiveStep] = useState(0);
  const [rows, setRows] = useState([]);
  const [submitError, setSubmitError] = useState('');

  const selectedType = findTeamType(type);

  useEffect(() => {
    const count = findTeamType(type)?.initialMatches || 0;
    setRows(Array.from({ length: count }, () => newRow()));
  }, [type]);

  const teamById = useMemo(
    () => new Map(teams.map((team) => [String(team.id), team])),
    [teams]
  );
  const teamNameFor = (teamId) => {
    if (!teamId) {
      return 'TBD';
    }
    if (teamId === 'walkOver') {
      return 'Walk over';
    }
    const team = teamById.get(teamId);
    return team ? team.name || 'Unnamed team' : 'Team';
  };

  const {
    duplicateTeams,
    unplacedTeams,
    filledMatches,
    walkovers
  } = summarizeTeamRows(teams, rows);
  const teamsFit = Boolean(selectedType) && teams.length > 0 && teams.length <= selectedType.capacity;
  const canSetupContinue = Boolean(name.trim()) && teamsFit;
  const canContinue = activeStep === 0 ? canSetupContinue : teamsFit && !duplicateTeams;
  const canGenerate = Boolean(name.trim()) && teamsFit && !duplicateTeams;

  const updateTeam = (rowId, side, value) => {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) {
          return row;
        }

        const next = { ...row, [side]: value };
        const other = side === 'team1Id' ? 'team2Id' : 'team1Id';
        if (value && next[other] === value) {
          next[other] = '';
        }
        if (side === 'team1Id' && !value && next.team2Id === 'walkOver') {
          next.team2Id = '';
        }
        return next;
      })
    );
  };

  const clearRow = (rowId) => {
    setRows((current) =>
      current.map((row) =>
        row.id === rowId ? { ...row, team1Id: '', team2Id: '' } : row
      )
    );
  };

  const fillFrom = (list) => {
    setRows((current) =>
      current.map((row, index) => {
        const first = list[index * 2];
        const second = list[index * 2 + 1];
        return {
          ...row,
          team1Id: first ? String(first.id) : '',
          team2Id: second ? String(second.id) : ''
        };
      })
    );
  };

  const clearAll = () => {
    setRows((current) => current.map((row) => ({ ...row, team1Id: '', team2Id: '' })));
  };

  const optionsFor = (row, side) => {
    const usedElsewhere = new Set();
    rows.forEach((entry) => {
      if (entry.id === row.id) {
        return;
      }
      if (entry.team1Id) {
        usedElsewhere.add(entry.team1Id);
      }
      if (entry.team2Id) {
        usedElsewhere.add(entry.team2Id);
      }
    });

    const otherSide = side === 'team1Id' ? 'team2Id' : 'team1Id';
    const options = teams
      .filter((team) => {
        const teamId = String(team.id);
        return !usedElsewhere.has(teamId) && teamId !== row[otherSide];
      })
      .map((team) => ({ value: String(team.id), label: team.name || 'Unnamed team' }));

    if (side === 'team2Id' && row.team1Id && row.team1Id !== 'walkOver') {
      options.push({ value: 'walkOver', label: 'Walk over' });
    }

    return options;
  };

  const handleGenerate = () => {
    if (!selectedType || !canGenerate) {
      return;
    }

    setSubmitError('');

    const resolveTeam = (teamId) => {
      if (teamId === 'walkOver' || teamId === 'TBD') {
        return teamId;
      }
      return teamById.get(teamId) || '';
    };

    dispatch(
      generateBracket({
        eventId: event.id,
        name: name.trim(),
        type,
        matches: rows.map((row) => ({
          team1: resolveTeam(row.team1Id),
          team2: resolveTeam(row.team2Id)
        }))
      })
    )
      .unwrap()
      .then((bracket) => {
        if (!bracket || !bracket.id) {
          setSubmitError('The bracket could not be created. Check the teams and try again.');
          return;
        }
        onSuccess();
      })
      .catch((message) => {
        setSubmitError(
          typeof message === 'string' && message
            ? message
            : 'The bracket could not be created. Please try again.'
        );
      });
  };

  const setupStep = (
    <Stack spacing={2.5}>
      <TextField
        label="Bracket name"
        value={name}
        onChange={(changeEvent) => setName(changeEvent.target.value)}
        fullWidth
        size="small"
        helperText="Shown on the bracket page and in exports."
      />

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Bracket size
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pick the knockout that fits this event&apos;s teams.
            </Typography>
          </Box>
          <Chip size="small" label={`${teams.length} teams`} />
        </Stack>

        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5
          }}
        >
          {TEAM_BRACKET_TYPES.map((entry) => (
            <BracketTypeCard
              key={entry.value}
              selected={type === entry.value}
              disabled={teams.length === 0 || teams.length > entry.capacity}
              label={entry.label}
              meta={`${entry.capacity} teams · ${entry.initialMatches} first-round matches`}
              roundsLabel={roundsLabel(entry.rounds)}
              onSelect={() => setType(entry.value)}
            />
          ))}
        </Box>

        {teams.length === 0 && (
          <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
            This event has no teams yet. Add teams to the event before generating a bracket.
          </Alert>
        )}

        {teams.length > 64 && (
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
            The largest standard bracket holds 64 teams; this event has {teams.length}.
          </Alert>
        )}

        {teamsFit && (
          <WizardHint>
            {teams.length} of {selectedType.capacity} slots will be filled; empty sides start as
            TBD.
          </WizardHint>
        )}
      </Box>

      {selectedType && (
        <Box sx={{ p: 1.75, border: 1, borderColor: 'divider', borderRadius: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Round plan
            </Typography>
            <Chip size="small" label={`${selectedType.rounds.length} rounds`} />
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {selectedType.rounds.map((round) => (
              <Chip
                key={round.name}
                size="small"
                variant="outlined"
                label={`${round.name} · ${round.matches}`}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );

  const matchupsStep = selectedType ? (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={1.5}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Seed the first round
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag to change the bracket order. A team can only appear once.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoFixHighIcon />}
            onClick={() => fillFrom(teams)}
            disabled={teams.length === 0}
          >
            Fill in order
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={() => fillFrom(shuffle(teams))}
            disabled={teams.length === 0}
          >
            Random draw
          </Button>
          <Button size="small" color="inherit" startIcon={<RestartAltIcon />} onClick={clearAll}>
            Clear
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={duplicateTeams ? 'error' : filledMatches === rows.length ? 'success' : 'default'}
          variant={duplicateTeams || filledMatches !== rows.length ? 'outlined' : 'filled'}
          icon={!duplicateTeams && filledMatches === rows.length ? <CheckIcon /> : undefined}
          label={`${filledMatches} of ${rows.length} matches with teams`}
        />
        {unplacedTeams > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`${unplacedTeams} ${unplacedTeams === 1 ? 'team' : 'teams'} unplaced`}
          />
        )}
        {walkovers > 0 && (
          <Chip size="small" variant="outlined" label={`${walkovers} walkover${walkovers === 1 ? '' : 's'}`} />
        )}
        {duplicateTeams && <Chip size="small" color="error" label="Team used twice" />}
      </Stack>

      <Box>
        <Reorder.Group
          as="div"
          axis="y"
          values={rows}
          onReorder={(next) => setRows(next)}
          style={{ margin: 0, padding: 0 }}
        >
          {rows.map((row, index) => (
            <TeamRow
              key={row.id}
              row={row}
              index={index}
              isLast={index === rows.length - 1}
              options1={optionsFor(row, 'team1Id')}
              options2={optionsFor(row, 'team2Id')}
              onTeamChange={updateTeam}
              onClear={clearRow}
            />
          ))}
        </Reorder.Group>

        <WizardHint>
          Drag the handle to change which side of the bracket a match sits on. A walkover
          advances team 1 automatically.
        </WizardHint>
      </Box>

      {duplicateTeams && (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
          A team is placed in more than one match.
        </Alert>
      )}
    </Stack>
  ) : (
    <Alert severity="warning">Choose a bracket size before seeding matches.</Alert>
  );

  const reviewStep = selectedType ? (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundImage: 'linear-gradient(135deg, rgba(255,235,59,0.16), rgba(255,235,59,0))'
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: '12px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'yellow.main',
              color: '#18181b'
            }}
          >
            <AccountTreeIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
              {name.trim()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedType.label} · {event.name}
            </Typography>
          </Box>
          <Chip size="small" color="success" variant="outlined" label="Ready" />
        </Stack>
      </Paper>

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Rounds
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {matchesLabel(selectedType.rounds)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {selectedType.rounds.map((round, index) => (
            <Chip
              key={round.name}
              size="small"
              label={`${round.name} · ${round.matches}`}
              sx={{ bgcolor: index === 0 ? 'action.selected' : 'transparent' }}
            />
          ))}
        </Stack>
      </Box>

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            First round
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top to bottom in bracket order
          </Typography>
        </Stack>
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          {rows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderBottom: index < rows.length - 1 ? 1 : 0,
                borderColor: 'divider'
              }}
            >
              <Typography
                variant="caption"
                sx={{ width: 20, flexShrink: 0, fontWeight: 700, color: 'text.secondary' }}
              >
                {index + 1}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 600,
                  color: row.team1Id ? 'text.primary' : 'text.disabled'
                }}
                noWrap
              >
                {teamNameFor(row.team1Id)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                vs
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 600,
                  textAlign: 'right',
                  color: row.team2Id ? 'text.primary' : 'text.disabled'
                }}
                noWrap
              >
                {teamNameFor(row.team2Id)}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Box>

      {unplacedTeams > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
          {unplacedTeams} team{unplacedTeams === 1 ? '' : 's'} will not be placed in this bracket.
          Empty sides start as TBD and can be filled on the bracket page.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        Generating creates the full tree. Team names can still be changed on the bracket page.
      </Typography>
    </Stack>
  ) : (
    <Alert severity="warning">Choose a bracket size first.</Alert>
  );

  return (
    <BracketWizard
      icon={<AccountTreeIcon />}
      eventName={event.name}
      steps={['Setup', 'Match-ups', 'Review']}
      activeStep={activeStep}
      onStepClick={(index) => setActiveStep(index)}
      error={submitError}
      generating={generating}
      canContinue={canContinue}
      canGenerate={canGenerate}
      onCancel={onCancel}
      onBack={() => setActiveStep((step) => Math.max(0, step - 1))}
      onNext={() => setActiveStep((step) => Math.min(2, step + 1))}
      onGenerate={handleGenerate}
    >
      {activeStep === 0 && setupStep}
      {activeStep === 1 && matchupsStep}
      {activeStep === 2 && reviewStep}
    </BracketWizard>
  );
};

export default GenerateBracketFromPlayers;
